import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const pricingTableSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().min(1).max(200),
  subtitle: z.string().max(400).optional(),
  billing_toggle: z.boolean().default(false).optional(),
  plans: z
    .array(
      z.object({
        name: z.string().min(1).max(60),
        description: z.string().max(200).optional(),
        price: z.string().min(1).max(40),
        period: z.string().max(40).optional(),
        features: z.array(z.object({ value: z.string().max(200) })).min(1).max(20),
        cta_label: z.string().min(1).max(60),
        cta_href: z.string().max(500),
        highlighted: z.boolean().default(false).optional(),
        badge: z.string().max(40).optional(),
      }),
    )
    .min(1)
    .max(5),
});

export type PricingTableData = z.infer<typeof pricingTableSchema>;

export function PricingTableRenderer({ eyebrow, h2, subtitle, plans }: PricingTableData) {
  return (
    <section className="v5-pricing">
      <div className="container">
        {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
        <h2 className="v5-h2">{h2}</h2>
        {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
        <div className="v5-pricing-grid" data-count={plans.length}>
          {plans.map((p, i) => (
            <div key={i} className={`v5-pricing-card ${p.highlighted ? 'is-highlighted' : ''}`}>
              {p.badge ? <span className="v5-pricing-badge">{p.badge}</span> : null}
              <div className="v5-pricing-name">{p.name}</div>
              {p.description ? <p className="v5-pricing-desc">{p.description}</p> : null}
              <div className="v5-pricing-amount">
                <span className="v5-pricing-price">{p.price}</span>
                {p.period ? <span className="v5-pricing-period">{p.period}</span> : null}
              </div>
              <ul className="v5-pricing-features">
                {p.features.map((f, j) => (
                  <li key={j}>
                    <span className="v5-pricing-check" aria-hidden>✓</span>
                    {typeof f === 'string' ? f : f.value}
                  </li>
                ))}
              </ul>
              <a className={p.highlighted ? 'v5-btn-primary' : 'v5-btn-outline'} href={p.cta_href}>
                {p.cta_label}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const pricingTableBlock: BlockDefinition<PricingTableData> = {
  meta: {
    type: 'pricing-table',
    label: 'Tabla de precios',
    description: 'Bloque de planes/precios con hasta 5 columnas, plan destacado y badge opcional.',
    category: 'cta',
    icon: 'tag',
  },
  schema: pricingTableSchema,
  defaults: {
    eyebrow: 'PRECIOS',
    h2: 'Elige el plan que mejor encaje',
    subtitle: 'Sin sorpresas. Cancela cuando quieras.',
    plans: [
      {
        name: 'Starter',
        description: 'Para empezar.',
        price: '19 €',
        period: '/mes',
        features: [{ value: '1 usuario' }, { value: 'Soporte por email' }, { value: 'Funciones esenciales' }],
        cta_label: 'Empezar',
        cta_href: '#',
      },
      {
        name: 'Pro',
        description: 'Para equipos.',
        price: '49 €',
        period: '/mes',
        features: [
          { value: '5 usuarios' },
          { value: 'Soporte prioritario' },
          { value: 'Todas las integraciones' },
          { value: 'Reporting avanzado' },
        ],
        cta_label: 'Probar 14 días',
        cta_href: '#',
        highlighted: true,
        badge: 'Más popular',
      },
      {
        name: 'Enterprise',
        description: 'Para empresas grandes.',
        price: 'A medida',
        features: [{ value: 'Usuarios ilimitados' }, { value: 'SLA y soporte 24/7' }, { value: 'SSO y auditoría' }],
        cta_label: 'Contactar',
        cta_href: '/contacto',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 400 },
    {
      name: 'plans',
      label: 'Planes',
      type: 'array',
      max: 5,
      itemFields: [
        { name: 'name', label: 'Nombre', type: 'text', required: true, max: 60 },
        { name: 'description', label: 'Descripción corta', type: 'text', max: 200 },
        { name: 'price', label: 'Precio', type: 'text', required: true, max: 40, placeholder: '49 €' },
        { name: 'period', label: 'Periodo', type: 'text', max: 40, placeholder: '/mes' },
        {
          name: 'features',
          label: 'Características (1 por línea)',
          type: 'array',
          itemFields: [
            { name: 'value', label: 'Texto', type: 'text', required: true, max: 200 },
          ],
        },
        { name: 'cta_label', label: 'Texto botón', type: 'text', required: true, max: 60 },
        { name: 'cta_href', label: 'URL botón', type: 'url', required: true },
        { name: 'highlighted', label: 'Plan destacado', type: 'boolean' },
        { name: 'badge', label: 'Badge (opcional)', type: 'text', max: 40, placeholder: 'Más popular' },
      ],
    },
  ],
  Renderer: PricingTableRenderer,
};
